/**
 * EventAvailabilityModal Component
 *
 * Read-only "See Availability" view for admins: every active roster member
 * and what they said about one event, grouped Available, Maybe, Not
 * available, No response. Same shell and rows as AssignCrewModal, without the
 * Assign button and without hiding people who are already crewed.
 */

import { useState, useEffect } from 'react';
import {
  getEventAvailabilityRoster,
  type EventAvailabilityRosterEntry,
} from '../../api/availability';
import { getErrorMessage } from '../../api/client';
import { AVAILABILITY_STATUS, type AvailabilityStatus } from '../../config/statusStyles';
import Modal from '../ui/Modal';
import NoteTooltip from '../ui/NoteTooltip';
import CrewProfileLink from '../crew/CrewProfileLink';

interface EventAvailabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  eventName: string;
  eventStatus: string;
}

type GroupKey = AvailabilityStatus | 'NO_RESPONSE';

/** Fixed section order. A note left without an answer is not a response. */
const GROUPS: { key: GroupKey; label: string }[] = [
  { key: 'AVAILABLE', label: 'Available' },
  { key: 'MAYBE', label: 'Maybe' },
  { key: 'NOT_AVAILABLE', label: 'Not available' },
  { key: 'NO_RESPONSE', label: 'No response' },
];

function groupOf(entry: EventAvailabilityRosterEntry): GroupKey {
  return entry.availability?.status ?? 'NO_RESPONSE';
}

const ORG_ROLE_LABEL: Record<EventAvailabilityRosterEntry['orgRole'], string | null> = {
  OWNER: 'Owner',
  ADMIN: 'Admin',
  CREW: null,
};

export default function EventAvailabilityModal({
  isOpen,
  onClose,
  eventId,
  eventName,
  eventStatus,
}: EventAvailabilityModalProps) {
  const [crew, setCrew] = useState<EventAvailabilityRosterEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadRoster();
    }
  }, [isOpen, eventId]);

  const loadRoster = async () => {
    setLoading(true);
    setError(null);
    try {
      const { crew } = await getEventAvailabilityRoster(eventId);
      setCrew(crew);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const filteredCrew = crew.filter((member) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      member.firstName.toLowerCase().includes(searchLower) ||
      member.lastName.toLowerCase().includes(searchLower) ||
      member.email.toLowerCase().includes(searchLower)
    );
  });

  const byName = (a: EventAvailabilityRosterEntry, b: EventAvailabilityRosterEntry) =>
    `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);

  const grouped = GROUPS.map((group) => ({
    ...group,
    members: filteredCrew.filter((m) => groupOf(m) === group.key).sort(byName),
  }));

  const responded = crew.filter((m) => groupOf(m) !== 'NO_RESPONSE').length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      labelledBy="event-availability-title"
      contentClassName="max-h-[80vh] flex flex-col"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <h2 id="event-availability-title" className="text-lg font-semibold text-gray-900">
              Crew Availability
            </h2>
            <p className="text-sm text-gray-500 truncate">{eventName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="mt-4">
          <input
            type="text"
            autoFocus
            placeholder="Search crew..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-rack focus:border-rack"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
            {error}
          </div>
        )}

        {eventStatus === 'DRAFT' && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-sm text-blue-800">
            Crew can't see this event until it's published, so they can't respond yet.
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse flex items-center gap-3 p-3">
                <div className="w-10 h-10 bg-gray-200"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {grouped.map((group) => (
              <section key={group.key} aria-labelledby={`availability-group-${group.key}`}>
                <h3
                  id={`availability-group-${group.key}`}
                  className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2"
                >
                  {group.label} ({group.members.length})
                </h3>
                {group.members.length === 0 ? (
                  <p className="text-sm text-gray-400 px-3 py-2">
                    {search ? 'No one matching your search' : 'No one'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {group.members.map((member) => {
                      const availabilityInfo = member.availability?.status
                        ? AVAILABILITY_STATUS[member.availability.status]
                        : null;
                      const roleLabel = ORG_ROLE_LABEL[member.orgRole];

                      return (
                        <div
                          key={member.id}
                          className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 border border-gray-100"
                        >
                          {/* Avatar */}
                          <div className="w-10 h-10 bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold">
                            {member.firstName.charAt(0)}
                            {member.lastName.charAt(0)}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5 flex-wrap">
                              <CrewProfileLink userId={member.id}>
                                {member.firstName} {member.lastName}
                              </CrewProfileLink>
                              {roleLabel && (
                                <span className="px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500 border border-gray-200">
                                  {roleLabel}
                                </span>
                              )}
                              {member.availability?.notes && (
                                <NoteTooltip
                                  note={member.availability.notes}
                                  label={`Availability note from ${member.firstName} ${member.lastName}`}
                                />
                              )}
                            </p>
                            <p className="text-xs text-gray-500 truncate">{member.email}</p>
                            {member.assignments.length > 0 && (
                              <p className="text-xs text-gray-600 mt-0.5">
                                {member.assignments.some((a) => a.status === 'CONFIRMED') ? 'Confirmed' : 'Assigned'}
                                {': '}
                                {member.assignments.map((a) => a.roleName).join(', ')}
                              </p>
                            )}
                          </div>

                          {/* Availability badge */}
                          {availabilityInfo ? (
                            <span
                              className={`px-2 py-0.5 text-xs font-medium ${availabilityInfo.className}`}
                            >
                              {availabilityInfo.label}
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500">
                              No Response
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>
            {loading ? '' : `${responded} of ${crew.length} responded`}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}
